import { Box, Chip, Divider, Grid, Typography } from "@mui/material";
import useColors from "../../../../api/hooks/useColors";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { icons, severities } from "../../../stack/environment/icons/iconExport";

import CustomToolTip from "../../../../components/CustomTooltip";
import LabelComponentProp from "../../../../components/LabelComponentProp";
import iconMapping from "../../../../icons/IconMapping";
import { ProtectionLevelText } from "../../../../utils/constants";
import IntegrationImage from "../../../stack/integrations/components/IntegrationImage";
import CoverageControlsLabel from "./CoverageControlsLabel";
import ProtectionLevel from "./ProtectionLevel";
import { protectionToolTipTitle } from "./StepsTable";

const StepDetails = ({ selectedStep, viewAttackPaths }) => {
  const Icon = iconMapping["Inventory"];
  const colors = useColors();

  return (
    <Grid item xs={12} mb={4} mt={2}>
      <Grid
        container
        gap={2}
        sx={{
          justifyContent: "flex-start",
          textAlign: "center",
          gap: 3,
        }}
      >
        <Grid item>
          <LabelComponentProp label={"Tools"} textTypography="caption">
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "8px",
              }}
            >
              {selectedStep?.cwe_id ? (
                <CustomToolTip title={"This step is associated with CVEs"}>
                  <Box>
                    <SvgToIcon IconLogo={icons.cveIcon} width={"24px"} />
                  </Box>
                </CustomToolTip>
              ) : selectedStep?.protection_level ===
                ProtectionLevelText.MISSING_CONTROLS ? (
                <CustomToolTip title={"This step has no protection in place"}>
                  <Box>
                    <SvgToIcon
                      IconLogo={icons.questionMarkIcon}
                      width={"24px"}
                    />
                  </Box>
                </CustomToolTip>
              ) : (
                selectedStep?.mitigating_controls_tools_set?.map(
                  (tool, index) => (
                    <IntegrationImage
                      key={`${tool}-${index}`}
                      name={tool}
                      width={"24px"}
                      newLogo={true}
                    />
                  )
                )
              )}
            </Box>
          </LabelComponentProp>
        </Grid>
        <Divider orientation="vertical" flexItem />
        <Grid item>
          <LabelComponentProp
            label={"Protection Level"}
            textTypography="caption"
            alignItems="flex-start"
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: "8px",
                maxWidth: "180px",
                flexShrink: 0,
              }}
            >
              <ProtectionLevel
                level={selectedStep.protection_level}
                shorten={true}
              />
              {selectedStep?.protection_level ===
                ProtectionLevelText.MISSING_CONTROLS || selectedStep?.cwe_id ? (
                <Chip
                  label={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography
                        variant="overline"
                        sx={{ color: colors.text_secondary }}
                      >
                        -
                      </Typography>
                    </Box>
                  }
                  sx={{
                    borderRadius: "12px",
                    background: "transparent",
                    border: "0.5px solid " + colors.grey_light,
                    alignItems: "center",
                    display: "flex",
                    padding: "5px 0px",
                    height: "24px",
                  }}
                />
              ) : (
                <CustomToolTip
                  title={protectionToolTipTitle(
                    selectedStep.protection_stats?.compliant,
                    selectedStep.protection_stats?.total
                  )}
                  placement="top"
                >
                  <Box>
                    <CoverageControlsLabel
                      compliant={selectedStep.protection_stats?.compliant}
                      total={selectedStep.protection_stats?.total}
                    />
                  </Box>
                </CustomToolTip>
              )}
            </Box>
          </LabelComponentProp>
        </Grid>
        <Divider orientation="vertical" flexItem />
        <Grid item>
          <LabelComponentProp
            alignItems="flex-start"
            label={"Impact"}
            textTypography="caption"
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Chip
                label={
                  (selectedStep?.misconfigured_entity_ids?.length || 0) +
                  " Entities"
                }
                icon={
                  <Icon style={{ color: colors.text_primary, width: "20px" }} />
                }
                style={{
                  background: colors.entity_chip_enabled_fill,
                  borderRadius: "8px",
                  fontSize: "12px",
                  color: colors.text_primary,
                  boxShadow: colors.entity_chip_inventory_shadow,
                  height: "24px",
                  paddingLeft: "4px",
                  cursor: "default",
                  userSelect: "none",
                }}
              />
            </Box>
          </LabelComponentProp>
        </Grid>
        <Divider orientation="vertical" flexItem />
        <Grid item>
          <LabelComponentProp
            label={"Exposure"}
            textTypography="caption"
            sx={{
              alignItems: "flex-start",
              gap: "4px",
            }}
          >
            {selectedStep.number_of_scenarios > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "24px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <CustomToolTip
                    title={`This step is involved in ${selectedStep.number_of_scenarios} attack paths, representing ${selectedStep.scenario_involvement_percentage}% of all attack paths in your attack surface`}
                  >
                    <Box
                      onClick={viewAttackPaths}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        cursor: "pointer",
                      }}
                    >
                      <SvgToIcon
                        IconLogo={severities.HighSeverityIcon}
                        style={{ width: "18px" }}
                      />
                      Part of {selectedStep.number_of_scenarios} attack paths
                      {selectedStep.scenario_involvement_percentage > 0 &&
                        " (" +
                          selectedStep.scenario_involvement_percentage +
                          "%)"}
                    </Box>
                  </CustomToolTip>
                  <Typography
                    onClick={viewAttackPaths}
                    style={{
                      marginTop: "6px",
                      cursor: "pointer",
                    }}
                  >
                    <SvgToIcon IconLogo={icons.arrow} width={"18px"} />
                  </Typography>
                </Box>
              </Box>
            ) : (
              "N/A"
            )}
          </LabelComponentProp>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default StepDetails;
